import React from 'react'
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack, Typography } from '@mui/material'
import { formatBudgetLeft, formatEstimateMoney } from '../../utils/themeAICost'
import type { MoneyUnit } from '../../tokenbudgets/model'

export interface JunkCheckConfirmDetails {
  /** Every answer on the current data: the check reads all of them. */
  responseCount: number
  /** Answers the free rules already flag. */
  ruleFlagged: number
  /** Answers the model reads: the rest. */
  modelCandidates: number
  estimatedTokens: number
  estimatedCost: number
  remainingBudget?: number
  /** Credits for a non-Manager (themeAIReaderUnit); absent for a Manager, who reads pounds. */
  moneyUnit?: MoneyUnit
  /** A fixed-mode list price: "3 × 100 responses". */
  priceBasis?: string
}

interface JunkCheckConfirmDialogProps {
  open: boolean
  /** The estimate is still loading. */
  loading: boolean
  /** The check is being started. */
  starting: boolean
  details?: JunkCheckConfirmDetails
  onCancel: () => void
  onProceed: () => void
}

// What the check will do and spend, before it spends (the same go/no-go the
// run has). The rules pass is free; only the model pass costs, and on a clean
// pool that can be more than it saves, so the dialog says so.
const JunkCheckConfirmDialog: React.FC<JunkCheckConfirmDialogProps> = ({ open, loading, starting, details, onCancel, onProceed }) => (
  <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
    <DialogTitle>Check for junk answers</DialogTitle>
    <DialogContent>
      {loading || !details ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Stack spacing={1.5}>
          <DialogContentText variant="body2">
            Free rules flag empty answers, random characters and “n/a”, and answers copied many times. A small model then reads the
            rest. Only the model reading spends budget, and nothing is changed until you decide. On clean data the check can cost
            more than it saves.
          </DialogContentText>
          <Row label="Answers to check" value={details.responseCount.toLocaleString()} />
          <Row label="Flagged already by the rules" value={details.ruleFlagged.toLocaleString()} />
          <Row label="Read by the model" value={details.modelCandidates.toLocaleString()} />
          <Row label="Estimated tokens" value={`~${details.estimatedTokens.toLocaleString()}`} />
          <Row label="Estimated budget spend" value={formatEstimateMoney(details.estimatedCost, details.moneyUnit)} emphasize />
          {details.priceBasis && <Row label="Price list" value={details.priceBasis} />}
          {typeof details.remainingBudget === 'number' && (
            <Row label="Budget left" value={formatBudgetLeft(details.remainingBudget, details.moneyUnit)} />
          )}
        </Stack>
      )}
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      {/* Named in full so a walkthrough's click on it cannot land on the offer's
          Cancel, which stops a running check. */}
      <Button onClick={onCancel} color="inherit" aria-label="Cancel, close without checking">Cancel</Button>
      <Button variant="contained" color="info" disabled={loading || starting || !details} onClick={onProceed}>
        Proceed
      </Button>
    </DialogActions>
  </Dialog>
)

const Row: React.FC<{ label: string; value: string; emphasize?: boolean }> = ({ label, value, emphasize }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: emphasize ? 700 : 500, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
  </Box>
)

export default JunkCheckConfirmDialog
